import { keepPreviousData } from "@tanstack/react-query";

import { useAuth } from "../../context/auth-context";
import type { IsoDateRange } from "../adminExport/datePresets";
import type {
  AdminSignupsDataEnvelope,
  AdminSignupsDataPayload,
  AdminSignupsSummary,
  AdminSignupsSummaryEnvelope,
  SignupKycStatusFilter,
} from "./adminSignups";
import { apiQueryKey } from "./queryKeys";
import { useApiQuery } from "./useApiQuery";

export const ADMIN_SIGNUPS_SUMMARY_URL = "api/admin/signups/summary";
export const ADMIN_SIGNUPS_DATA_URL = "api/admin/signups/data";

/** Omit the range to get the backend default (last 30 days). */
export function useAdminSignupsSummaryQuery(range: IsoDateRange | null) {
  const { isAuthenticated } = useAuth();

  return useApiQuery<AdminSignupsSummaryEnvelope, AdminSignupsSummary>({
    queryKey: apiQueryKey("admin-signups-summary", {
      startDate: range?.startDate ?? null,
      endDate: range?.endDate ?? null,
    }),
    url: ADMIN_SIGNUPS_SUMMARY_URL,
    params: { startDate: range?.startDate, endDate: range?.endDate },
    enabled: Boolean(isAuthenticated),
    select: (body) => body.data,
  });
}

export type UseAdminSignupsDataParams = {
  page: number;
  limit: number;
  range: IsoDateRange | null;
  search?: string;
  kycStatus?: SignupKycStatusFilter;
};

export function useAdminSignupsDataQuery({
  page,
  limit,
  range,
  search,
  kycStatus = "",
}: UseAdminSignupsDataParams) {
  const { isAuthenticated } = useAuth();
  const term = search?.trim() || undefined;

  return useApiQuery<AdminSignupsDataEnvelope, AdminSignupsDataPayload>({
    queryKey: apiQueryKey("admin-signups-data", {
      page,
      limit,
      startDate: range?.startDate ?? null,
      endDate: range?.endDate ?? null,
      search: term ?? null,
      kycStatus: kycStatus || null,
    }),
    url: ADMIN_SIGNUPS_DATA_URL,
    params: {
      page,
      limit,
      startDate: range?.startDate,
      endDate: range?.endDate,
      search: term,
      kycStatus: kycStatus || undefined,
    },
    enabled: Boolean(isAuthenticated),
    placeholderData: keepPreviousData,
    select: (body) => body.data,
  });
}
